import { useEffect, useState } from "react";

import { Box, Button, Checkbox, CheckboxGroup, Divider, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, Text, Textarea, useDisclosure, useToast } from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";

import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectReviewer from "@/components/Selects/SelectReviewer";
import SelectApprovalStatus from "@/components/Selects/SelectApprovalStatus";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { reviewInspectorSchema } from "@/schemas/reviewInspectorSchema";
import { downloadFile } from "@/services/downloadService";
import { getInspectorById, updateStatusInspector } from "@/services/inspectorService";
import Loader from "@/components/Layouts/Loader";

import '../styles/Form.css';

const InspectorReview = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const location = useLocation();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, returnPath } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Update Status?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Update Status?");
        setConfirmationTest("Are you sure you want to update?");
        setConfirmationTitleButton("Update Status");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataUpdate = () => {
        const obj = {
            inspectorId: id,
            note: dataEdit.note || '',
            registerStatus: parseInt(dataEdit.status),
            modifiedBy: name || "System"
        };
        return obj;
    }

    const handleConfirm = async () => {
        onClose();
        if (title === "Cancel to Update Status?") {
            navigate(returnPath || '/manage-inspecting-company');
            return;
        }

        const obj = generateDataUpdate();
        const response = await updateStatusInspector(obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Status updated.',
                description: "We've updated the status for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate(returnPath || '/manage-inspecting-company')
            }, 1000);
        } else {
            toast({
                title: 'Status failed to update.',
                description: "We unabled to update the status for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const getDataDetail = async () => {
        setIsLoading(true);
        try {
            const response = await getInspectorById(id) as any;
            if (response?.status === HttpStatusCode.Forbidden) {
                toast({
                    title: response?.status,
                    description: response?.message,
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                return;
            }

            if (response) {
                setDataDetail(response.data);
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        } else {
            setIsLoading(false);
        }
    }, [id])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            {isLoading ? <Loader /> : <div className="form-container">
                <Formik
                    initialValues={{
                        companyName: dataDetail?.companyName ?? '',
                        type: dataDetail ? [dataDetail?.sirebire ? 'SIRE | BIRE' : '', dataDetail?.vetting ? 'Vetting' : ''] : [],
                        reviewer: dataDetail?.isReviewer ? 'true' : 'false',
                        address: dataDetail?.address ?? '',
                        country: dataDetail?.country ?? '',
                        contactPerson: dataDetail?.contactPerson ?? '',
                        email: dataDetail?.email ?? '',
                        alternateEmail: dataDetail?.alternateEmail ?? '',
                        phone: dataDetail?.phone ?? '',
                        status: dataDetail?.status === 1 ? "2" : dataDetail?.status.toString() ?? '',
                        note: dataDetail?.note ?? ''
                    }}
                    validationSchema={reviewInspectorSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='companyName'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.companyName && form.touched.companyName} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Company Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Company Name' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.companyName}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='type'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.type && form.touched.type} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CheckboxGroup
                                                    value={props.values.type}
                                                    onChange={(value: string[]) => props.setFieldValue('type', value)}
                                                    isDisabled
                                                >
                                                    <Flex gap={8}>
                                                        <Checkbox value="SIRE | BIRE">SIRE | BIRE</Checkbox>
                                                        <Checkbox value="Vetting">Vetting</Checkbox>
                                                    </Flex>
                                                </CheckboxGroup>
                                                <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='reviewer'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.reviewer && form.touched.reviewer} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Reviewer<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectReviewer name='reviewer' handleChange={({ value }: any) => props.setFieldValue('reviewer', value)} value={props.values.reviewer} hasError={form.errors.reviewer && form.touched.reviewer} defaultValue={props.values.reviewer} isDisabled />
                                                <FormErrorMessage>{form.errors.reviewer}</FormErrorMessage>
                                            </div>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='address'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.address && form.touched.address} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <Textarea {...field} placeholder='Address' className="form-input" isDisabled />
                                        </div>
                                        <FormErrorMessage>{form.errors.address}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.country && form.touched.country} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} defaultValue={props.values.country} isDisabled />
                                                <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='contactPerson'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.contactPerson && form.touched.contactPerson} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Contact Person' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.contactPerson}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='email'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.email && form.touched.email} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Nomination Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Nomination Email' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='alternateEmail'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.alternateEmail && form.touched.alternateEmail} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Invoice Email' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.alternateEmail}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='phone'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.phone && form.touched.phone} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Phone Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Phone Number' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.phone}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='dgt1'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.cor && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">DGT-1<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Box
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                    padding={4}
                                                    backgroundColor={dataDetail?.dgT1 ? "green.50" : "yellow.50"}
                                                    borderColor={dataDetail?.dgT1 ? "green.200" : "yellow.200"}
                                                    width="100%"
                                                    marginTop="15px"
                                                >
                                                    <HStack
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={3}
                                                        backgroundColor="white"
                                                        justifyContent="space-between"
                                                        width="100%"
                                                    >
                                                        <HStack>
                                                            <Text>{dataDetail?.dgT1 ? 'DGT-1 File' : 'No file uploaded'}</Text>
                                                        </HStack>
                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.dgT1)}>
                                                            <Icon as={FaEye} />
                                                            <Text>Download Document</Text>
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                                <FormErrorMessage>{form.errors.dgt1}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='cor'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.cor && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">COR<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Box
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                    padding={4}
                                                    backgroundColor={dataDetail?.cor ? "green.50" : "yellow.50"}
                                                    borderColor={dataDetail?.cor ? "green.200" : "yellow.200"}
                                                    width="100%"
                                                    marginTop="15px"
                                                >
                                                    <HStack
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={3}
                                                        backgroundColor="white"
                                                        justifyContent="space-between"
                                                        width="100%"
                                                    >
                                                        <HStack>
                                                            <Text>{dataDetail?.cor ? 'COR File' : 'No file uploaded'}</Text>
                                                        </HStack>
                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.cor)}>
                                                            {dataDetail?.cor && <Icon as={FaEye} />}
                                                            <Text>{dataDetail?.cor ? 'Download Document' : ''}</Text>
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                                <FormErrorMessage>{form.errors.cor}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='companyProfile'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.companyProfile && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Company Profile</FormLabel>
                                            <div className="form-input-container">
                                                <Box
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                    padding={4}
                                                    backgroundColor={dataDetail?.companyProfile ? "green.50" : "yellow.50"}
                                                    borderColor={dataDetail?.companyProfile ? "green.200" : "yellow.200"}
                                                    width="100%"
                                                    marginTop="15px"
                                                >
                                                    <HStack
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={3}
                                                        backgroundColor="white"
                                                        justifyContent="space-between"
                                                        width="100%"
                                                    >
                                                        <HStack>
                                                            <Text>{dataDetail?.companyProfile ? 'Company Profile File' : 'No file uploaded'}</Text>
                                                        </HStack>
                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.companyProfile)}>
                                                            {dataDetail?.companyProfile && <Icon as={FaEye} />}
                                                            <Text>{dataDetail?.companyProfile ? 'Download Document' : ''}</Text>
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                                <FormErrorMessage>{form.errors.companyProfile}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='lapKeuAkhir'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.lapKeuAkhir && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Latest Financial Report</FormLabel>
                                            <div className="form-input-container">
                                                <Box
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                    padding={4}
                                                    backgroundColor={dataDetail?.financeReports ? "green.50" : "yellow.50"}
                                                    borderColor={dataDetail?.financeReports ? "green.200" : "yellow.200"}
                                                    width="100%"
                                                    marginTop="15px"
                                                >
                                                    <HStack
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={3}
                                                        backgroundColor="white"
                                                        justifyContent="space-between"
                                                        width="100%"
                                                    >
                                                        <HStack>
                                                            <Text>{dataDetail?.companyProfile ? 'Latest Financial Report File' : 'No file uploaded'}</Text>
                                                        </HStack>
                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.financeReports)}>
                                                            {dataDetail?.financeReports && <Icon as={FaEye} />}
                                                            <Text>{dataDetail?.financeReports ? 'Download Document' : ''}</Text>
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                                <FormErrorMessage>{form.errors.lapKeuAkhir}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='bankDetails'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.bankDetails && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Bank Details</FormLabel>
                                            <div className="form-input-container">
                                                <Box
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                    padding={4}
                                                    backgroundColor={dataDetail?.bankDetail ? "green.50" : "yellow.50"}
                                                    borderColor={dataDetail?.bankDetail ? "green.200" : "yellow.200"}
                                                    width="100%"
                                                    marginTop="15px"
                                                >
                                                    <HStack
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={3}
                                                        backgroundColor="white"
                                                        justifyContent="space-between"
                                                        width="100%"
                                                    >
                                                        <HStack>
                                                            <Text>{dataDetail?.bankDetail ? 'Bank Details File' : 'No file uploaded'}</Text>
                                                        </HStack>
                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.bankDetail)}>
                                                            {dataDetail?.bankDetail && <Icon as={FaEye} />}
                                                            <Text>{dataDetail?.bankDetail ? 'Download Document' : ''}</Text>
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                                <FormErrorMessage>{form.errors.bankDetails}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='NIB'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.NIB && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">NIB / Company Bussiness Registration</FormLabel>
                                            <div className="form-input-container">
                                                <Box
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                    padding={4}
                                                    backgroundColor={dataDetail?.nibBusReg ? "green.50" : "yellow.50"}
                                                    borderColor={dataDetail?.nibBusReg ? "green.200" : "yellow.200"}
                                                    width="100%"
                                                    marginTop="15px"
                                                >
                                                    <HStack
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={3}
                                                        backgroundColor="white"
                                                        justifyContent="space-between"
                                                        width="100%"
                                                    >
                                                        <HStack>
                                                            <Text>{dataDetail?.nibBusReg ? 'NIB File' : 'No file uploaded'}</Text>
                                                        </HStack>
                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.nibBusReg)}>
                                                            {dataDetail?.nibBusReg && <Icon as={FaEye} />}
                                                            <Text>{dataDetail?.nibBusReg ? 'Download Document' : ''}</Text>
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                                <FormErrorMessage>{form.errors.NIB}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='status'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <SelectApprovalStatus name='status' handleChange={({ value }: any) => props.setFieldValue('status', value)} value={props.values.status} hasError={form.errors.status && form.touched.status} defaultValue={props.values.status} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {props.values.status === "4" &&
                                <Field name='note'>
                                    {({ field, form }: any) => (
                                        <FormControl isInvalid={form.errors.note && form.submitCount > 0} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Note<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Textarea {...field} placeholder='Note' className="form-input" isInvalid={form.errors.note && form.submitCount > 0} />
                                                    <FormErrorMessage>{form.errors.note}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                >
                                    Update Status
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>}
        </>
    )
}

export default InspectorReview;
